import React, { useEffect, useState } from 'react';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import ViewAsSelect from 'components/view_as_select';
import { getPageViewAsSelection, storePageViewAsSelection } from 'utils/page_view_as_storage';

const SIGNED_OUT_VALUE = 'signed-out';
const SIGNED_IN_WITHOUT_MEMBERSHIP_VALUE = 'signed-in-without-membership';

const destinationFor = (linkType, pageId, externalUrl, pages) => {
  if (linkType === 'external_url' && isPresent(externalUrl)) {
    return externalUrl;
  }

  if (linkType === 'page' && isPresent(pageId)) {
    const page = pages.find(item => item.id === pageId);
    return page ? `/pages/${page.slug}` : null;
  }

  return null;
};

const membershipTypeValue = id => `membership-type:${id}`;

const navigateTo = (destination) => {
  if (/^https?:\/\//i.test(destination)) {
    window.location.assign(destination);
  } else if (destination !== window.location.pathname) {
    AppUI.routeStore.routeTo(destination);
  }
};

const optionsFor = (navigationItem, membershipTypes, pages) => {
  const options = [];

  const signedOutDestination = destinationFor(
    navigationItem.signedOutLinkType,
    navigationItem.signedOutPageId,
    navigationItem.signedOutExternalUrl,
    pages,
  );
  if (signedOutDestination) {
    options.push({
      value: SIGNED_OUT_VALUE,
      label: 'Signed Out',
      destination: signedOutDestination,
    });
  }

  const signedInDestination = destinationFor(
    navigationItem.signedInLinkType,
    navigationItem.signedInPageId,
    navigationItem.signedInExternalUrl,
    pages,
  );
  if (signedInDestination) {
    options.push({
      value: SIGNED_IN_WITHOUT_MEMBERSHIP_VALUE,
      label: 'Signed In without Membership',
      destination: signedInDestination,
    });
  }

  membershipTypes.forEach(membershipType => {
    const navigationSetting = navigationItem.navigationItemMembershipTypes
      ?.find(item => item.membershipTypeId === membershipType.id);
    const destination = navigationSetting && destinationFor(
      navigationSetting.linkType,
      navigationSetting.pageId,
      navigationSetting.externalUrl,
      pages,
    );

    if (destination) {
      options.push({
        value: membershipTypeValue(membershipType.id),
        label: membershipType.name,
        destination,
      });
    }
  });

  return options;
};

const initialValue = (options, page) => {
  const savedValue = getPageViewAsSelection().value;
  if (options.some(option => option.value === savedValue)) {
    return savedValue;
  }

  const currentMembershipTypeId = AppUI.user.currentUserGroup?.membershipTypeId;
  const currentMembershipIsForPage = page.membershipTypes
    ?.some(item => item.id === currentMembershipTypeId);
  const currentMembershipValue = membershipTypeValue(currentMembershipTypeId);

  if (currentMembershipIsForPage && options.some(option => option.value === currentMembershipValue)) {
    return currentMembershipValue;
  }

  return options[0]?.value ?? null;
};

export default function PageViewAsSelect({ membershipTypes, navigationItem, page, pages }) {
  const options = optionsFor(navigationItem, membershipTypes, pages);
  const [value, setValue] = useState(() => initialValue(options, page));
  const selectedValue = options.some(option => option.value === value)
    ? value
    : initialValue(options, page);
  const selectedDestination = options.find(option => option.value === selectedValue)?.destination;

  useEffect(() => {
    if (selectedValue) {
      storePageViewAsSelection(selectedValue, navigationItem.id);
    }
  }, [navigationItem.id, selectedValue]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const loadedPageMatchesPath = currentPath === `/pages/${page.slug}` ||
      currentPath === `/pages/${page.id}`;

    if (selectedDestination && loadedPageMatchesPath) {
      navigateTo(selectedDestination);
    }
  }, [page.id, page.slug, selectedDestination]);

  const onChange = (nextValue) => {
    const option = options.find(item => item.value === nextValue);
    if (!option) { return; }

    setValue(nextValue);
    storePageViewAsSelection(nextValue, navigationItem.id);
    navigateTo(option.destination);
  };

  return (
    <ViewAsSelect
      options={options}
      value={selectedValue}
      onChange={onChange}
    />
  );
}
